// owner: applications-domain agent — zod schemas + inferred types shared by api and web
import { z } from 'zod';
import { errors } from '../errors';
import { EpochMsSchema, PaginationQuerySchema, UlidSchema, paginated } from './common';
import { MatchMethodSchema, MatchEvidenceSchema } from './matching';
import { JobSchema } from './jobs';

export const APPLICATION_STATUSES = [
  'submitted',
  'reviewing',
  'shortlisted',
  'interviewing',
  'offered',
  'hired',
  'rejected',
  'withdrawn',
] as const;
export const ApplicationStatusSchema = z.enum(APPLICATION_STATUSES);
export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>;

/** Irreversible outward effects: an agent principal needs human approval (spec R-063). */
export const GATED_APPLICATION_STATUSES: readonly ApplicationStatus[] = ['offered', 'hired'];

export type ApplicationActor = 'candidate' | 'employer';

// Pipeline: submitted -> reviewing -> shortlisted -> interviewing -> offered -> hired.
// Stages cannot be skipped or reversed; employers may reject at any live stage;
// candidates may only withdraw. hired / rejected / withdrawn are terminal.
const EMPLOYER_TRANSITIONS: Record<ApplicationStatus, readonly ApplicationStatus[]> = {
  submitted: ['reviewing', 'rejected'],
  reviewing: ['shortlisted', 'interviewing', 'rejected'],
  shortlisted: ['interviewing', 'rejected'],
  interviewing: ['offered', 'rejected'],
  offered: ['hired', 'rejected'],
  hired: [],
  rejected: [],
  withdrawn: [],
};

export const isTerminal = (s: ApplicationStatus): boolean =>
  s === 'hired' || s === 'rejected' || s === 'withdrawn';

export function allowedTransitions(
  from: ApplicationStatus,
  actor: ApplicationActor,
): readonly ApplicationStatus[] {
  if (isTerminal(from)) return [];
  return actor === 'candidate' ? ['withdrawn'] : EMPLOYER_TRANSITIONS[from];
}

export const canTransition = (
  from: ApplicationStatus,
  to: ApplicationStatus,
  actor: ApplicationActor,
): boolean => allowedTransitions(from, actor).includes(to);

export function assertTransition(
  from: ApplicationStatus,
  to: ApplicationStatus,
  actor: ApplicationActor,
): void {
  if (from === to) throw errors.conflict(`Application is already '${from}'`, { from, to });
  if (canTransition(from, to, actor)) return;
  const allowed = allowedTransitions(from, actor);
  throw errors.conflict(
    `Cannot move application from '${from}' to '${to}' as ${actor}. ${
      allowed.length ? `Allowed: ${allowed.join(', ')}.` : `'${from}' is a final status.`
    }`,
    { from, to, actor, allowed },
  );
}

// ───────── request schemas ─────────
export const ApplyBodySchema = z.object({
  cover: z.string().trim().max(5000).default(''),
  /** A resume the applicant already uploaded via POST /api/v1/profiles/me/resumes (must be theirs). */
  resumeMediaId: UlidSchema.optional(),
});
export const ApplicationIdParamSchema = z.object({ id: UlidSchema });
export const JobApplicationsParamSchema = z.object({ id: UlidSchema });

export const StatusUpdateSchema = z.object({
  status: ApplicationStatusSchema,
  note: z.string().trim().min(1).max(2000).optional(),
  /** An agent retries a gated transition (offered/hired) with the id of the human-approved request. */
  approvalId: UlidSchema.optional(),
});
export const BulkStatusSchema = z.object({
  ids: z.array(UlidSchema).min(1).max(50),
  status: ApplicationStatusSchema,
  note: z.string().trim().min(1).max(2000).optional(),
});
export const NoteCreateSchema = z.object({ body: z.string().trim().min(1).max(4000) });

export const MyApplicationsQuerySchema = PaginationQuerySchema.extend({
  status: ApplicationStatusSchema.optional(),
});
export const JobApplicationsQuerySchema = PaginationQuerySchema.extend({
  status: ApplicationStatusSchema.optional(),
  minScore: z.coerce.number().min(0).max(100).optional(),
});

// ───────── responses ─────────
export const ApplicationSchema = z.object({
  id: UlidSchema,
  jobId: UlidSchema,
  userId: UlidSchema,
  status: ApplicationStatusSchema,
  cover: z.string(),
  score: z.number().nullable(),
  scoreMethod: MatchMethodSchema.nullable(),
  source: z.enum(['human', 'agent']),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
});
export type Application = z.infer<typeof ApplicationSchema>;

export const JobSummarySchema = JobSchema.pick({
  id: true,
  slug: true,
  title: true,
  status: true,
  org: true,
  location: true,
  remote: true,
});
export const CandidateSummarySchema = z.object({
  userId: UlidSchema,
  displayName: z.string(),
  handle: z.string().nullable(),
  headline: z.string(),
  location: z.string(),
});

export const MyApplicationSchema = ApplicationSchema.extend({
  job: JobSummarySchema,
  allowedNext: z.array(ApplicationStatusSchema),
});
export const MyApplicationsSchema = paginated(MyApplicationSchema);

export const EmployerApplicationSchema = ApplicationSchema.extend({
  candidate: CandidateSummarySchema,
  allowedNext: z.array(ApplicationStatusSchema),
});
export const EmployerApplicationsSchema = paginated(EmployerApplicationSchema);

export const StatusHistoryEntrySchema = z.object({
  fromStatus: ApplicationStatusSchema.nullable(),
  toStatus: ApplicationStatusSchema,
  actorUserId: UlidSchema.nullable(),
  actorKind: z.enum(['user', 'agent']),
  note: z.string().nullable(),
  createdAt: EpochMsSchema,
});
/** Same shape for candidate and employer views; `viewer` says which one the caller is. */
export const ApplicationDetailSchema = ApplicationSchema.extend({
  viewer: z.enum(['candidate', 'employer']),
  job: JobSummarySchema,
  candidate: CandidateSummarySchema,
  allowedNext: z.array(ApplicationStatusSchema),
  history: z.array(StatusHistoryEntrySchema),
});

export const NoteSchema = z.object({
  id: UlidSchema,
  applicationId: UlidSchema,
  authorUserId: UlidSchema,
  authorKind: z.enum(['user', 'agent']),
  body: z.string(),
  createdAt: EpochMsSchema,
});
export const NotesSchema = z.object({ items: z.array(NoteSchema) });

export const BulkStatusResultSchema = z.object({
  results: z.array(
    z.discriminatedUnion('ok', [
      z.object({ id: UlidSchema, ok: z.literal(true), status: ApplicationStatusSchema }),
      z.object({
        id: UlidSchema,
        ok: z.literal(false),
        code: z.string(),
        message: z.string(),
        approvalId: UlidSchema.optional(),
      }),
    ]),
  ),
});

export const ScreeningSchema = z.object({
  applicationId: UlidSchema,
  score: z.number().int().min(0).max(100),
  method: MatchMethodSchema,
  reasons: z.array(z.string()),
  evidence: MatchEvidenceSchema,
});
